/**
 * Amylo 솔루션 상세 페이지 · 영상 분석(아밀로이드 PET · Centiloid)
 * 내용은 제품팀 소개 페이지(2026-10-23)를 옮긴 것. 그래픽 수치는 예시.
 */
import { createFileRoute } from '@tanstack/react-router';
import React from 'react';
import { PublicLayout } from '../../components/public-layout.js';
import { SolutionLayout } from '../../components/solution-layout.js';
import {
  BarList,
  CardGrid,
  CompareCards,
  DemoCta,
  DistributionChart,
  DeviceInfo,
  FaqSection,
  FeatureRow,
  Figure,
  HeroVisual,
  IntegrationSection,
  MiniBars,
  ExportList,
  OvLabel,
  OvValue,
  ProdSection,
  ReportSheet,
  SampleNote,
  Stat,
  TrendChart,
} from '../../components/product-page.js';
import { DEMO_HREF, DEVICE_DISCLAIMER } from '../../lib/product-content.js';

/** 정상군 · 알츠하이머병 환자군 Centiloid 분포(예시 삽화)와 이번 검사 값 */
const DIST = {
  value: 37.5,
  min: -20,
  max: 150,
  ticks: [0, 50, 100, 150],
  unit: 'CL',
  groups: [
    { label: '정상군', mean: 2, sd: 11, peak: 0.95, tone: 'a' as const },
    { label: '알츠하이머병 환자군', mean: 92, sd: 32, peak: 0.6, tone: 'b' as const },
  ],
};

const REGIONS = [
  { label: '전두엽', value: 46.2 },
  { label: '두정엽', value: 16.8 },
  { label: '측두엽', value: 43.0 },
  { label: '후두엽', value: 26.5 },
  { label: '대상회', value: 23.3 },
  { label: '선조체', value: 51.7 },
];

function Page() {
  return (
    <PublicLayout disclaimerItems={DEVICE_DISCLAIMER}>
      <SolutionLayout
        stage="img"
        badgeLabel="IMG · 영상 분석"
        certs={['식약처 인증 D제인 24-278호']}
        domain="아밀로이드 정량 · PET-CT · PET-MRI"
        name="Amylo"
        tagline="아밀로이드 PET를 표준 단위 Centiloid로"
        description={<><strong>PET-CT · PET-MRI</strong> 영상에서 전체와 영역별 Centiloid를 산출하고, 검사 시기별 변화를 비교합니다.</>}
        cta={{ label: '데모 신청', href: DEMO_HREF }}
        visual={
          <HeroVisual
            title="Amylo · SUVR Map Viewer"
            src="/solutions/amylo-hero.png"
            alt="Amylo SUVR Map 뷰어"
            caption="SUVR Map 뷰어 · 샘플 데이터"
            bl={
              <>
                <OvLabel>GLOBAL CENTILOID</OvLabel>
                <OvValue value="37.5" unit="CL" />
                <DistributionChart {...DIST} compact />
              </>
            }
            tr={
              <>
                <OvLabel>영역별 CL</OvLabel>
                <MiniBars
                  max={60}
                  rows={[
                    { label: '선조체', value: 51.7 },
                    { label: '전두엽', value: 46.2 },
                    { label: '측두엽', value: 43.0 },
                  ]}
                />
              </>
            }
          />
        }
        crossSell={[
          { href: '/solutions/morph', name: 'Morph · Morph+', desc: '진료용 MRI로 보는 뇌 위축', stage: 'img' },
          { href: '/solutions/morph-ct', name: 'Morph CT · CT+', desc: 'MRI 없이 CT로 보는 뇌 위축', stage: 'img' },
          { href: '/solutions/scst', name: 'SCST', desc: '인지 영역별 표준화 점수', stage: 'dx' },
        ]}
        after={
          <>
            <DemoCta name="Amylo" />
            <DeviceInfo
              keys={['제품명', '모델명', '제품코드(등급)', '인허가', '제조원']}
              columns={[
                {
                  name: 'Amylo',
                  rows: {
                    제품명: 'Amylo',
                    모델명: 'BBA',
                    '제품코드(등급)': 'A1AXXA1 (2등급)',
                    인허가: '식약처 디지털의료기기 제조 인증 D제인 24-278호',
                    제조원: '(주)뷰브레인헬스케어',
                  },
                },
              ]}
            />
          </>
        }
      >
        {/* ── 개요 ─────────────────────────────────────────────── */}
        <ProdSection label="Overview" title="아밀로이드 PET 영상을 정량 수치로 제시합니다">
          <CardGrid
            cols={3}
            items={[
              { title: '표준 단위로 비교', desc: 'Centiloid로 장비 · 검사약 · 기관이 달라도 같은 척도로 비교합니다.' },
              { title: '전체와 영역별 수치', desc: 'Global과 선조체를 포함한 6개 영역의 Centiloid를 함께 산출합니다.' },
              { title: '검사 시기별 변화', desc: '같은 환자의 이전 검사와 수치 · 영상 차이를 한 화면에서 비교합니다.' },
            ]}
          />
        </ProdSection>

        {/* ── 주요 기능 ─────────────────────────────────────────── */}
        <ProdSection alt label="Features" title="주요 기능">
          <FeatureRow
            no="01"
            title="Centiloid (CL) 자동 정량"
            desc="PET-CT · PET-MRI 영상을 환자 본인의 CT 또는 MRI에 정합해 전체와 6개 영역의 Centiloid를 산출합니다. FBB · FMM 두 검사약을 같은 척도로 비교합니다."
            tags={['Global CL', '6개 영역별 CL', 'FBB · FMM']}
          >
            <Figure>
              <div className="prod-grid-2" style={{ gap: 24, alignItems: 'start' }}>
                <div>
                  <div className="prod-figure-head" style={{ marginBottom: 12 }}>GLOBAL CENTILOID</div>
                  <OvValue value="37.5" unit="CL" xl />
                  <div style={{ marginTop: 16 }}>
                    <DistributionChart {...DIST} />
                  </div>
                </div>
                <div>
                  <div className="prod-figure-head" style={{ marginBottom: 12 }}>영역별 CENTILOID</div>
                  <BarList rows={REGIONS} max={60} />
                </div>
              </div>
            </Figure>
          </FeatureRow>

          <FeatureRow
            no="02"
            reverse
            title="정량 분석 리포트"
            desc="영역별 축적 정도를 시각화한 리포트를 자동 생성합니다. 임상 해석은 담당의 판단 영역입니다."
          >
            <Figure>
              <ReportSheet
                title="Amylo 정량 분석 리포트"
                page="1 / 5"
                meta={[
                  { k: '검사', v: 'PET-CT' },
                  { k: '검사약', v: 'FBB' },
                  { k: '참조 영역', v: 'Whole Cerebellum' },
                ]}
                global={{ k: 'Global CL', v: '37.5' }}
                chart={<DistributionChart {...DIST} compact />}
                regions={REGIONS}
              />
              <ExportList />
            </Figure>
          </FeatureRow>

          <FeatureRow
            no="03"
            title="시기별 변화 추적"
            desc="같은 환자의 검사 시기별 CL 수치와 두 시점 차이를 한 화면에서 비교합니다."
            tags={['시기별 CL 추이', '두 시점 차이 영상']}
          >
            <Figure head="GLOBAL CENTILOID · 검사 시기별">
              <TrendChart
                points={[
                  { x: '2025.03', y: 52.4, label: '52.4' },
                  { x: '2025.09', y: 44, label: '44' },
                  { x: '2026.03', y: 37.5, label: '37.5' },
                ]}
                yMin={20}
                yMax={60}
                ticks={[20, 40, 60]}
              />
              <div className="prod-grid-2">
                <Stat label="첫 검사 대비" value="−14.9" unit="CL" />
                <Stat label="직전 검사 대비" value="−6.5" unit="CL" />
              </div>
            </Figure>
          </FeatureRow>
          <SampleNote />
        </ProdSection>

        {/* ── 활용 환경 ─────────────────────────────────────────── */}
        <ProdSection label="Use Cases" title="이런 환경에서 활용합니다">
          <CardGrid
            cols={3}
            items={[
              { kicker: '검사 · 판독', title: '아밀로이드 PET 검사기관 · 핵의학과 · 영상의학과', desc: 'PACS로 받은 PET 영상을 판독 흐름 안에서 바로 정량합니다.' },
              { kicker: '진료', title: '신경과 · 치매 진료', desc: 'Centiloid 결과지를 진료와 환자 설명에 참고합니다.' },
              { kicker: '외부 영상', title: 'PET 장비가 없는 병 · 의원', desc: '외부에서 받은 PET과 CT · MRI를 업로드해 결과를 확인합니다.' },
            ]}
          />
        </ProdSection>

        {/* ── PET-CT · PET-MRI 비교 ─────────────────────────────── */}
        <ProdSection alt label="PET-CT · PET-MRI" title="PET-CT도, PET-MRI도 같은 기준으로">
          <CompareCards
            items={[
              { name: 'PET-CT', input: 'Amyloid PET + 감쇠보정 CT', output: '영역별 Centiloid', foot: '추가 MRI 촬영 없이 분석' },
              { name: 'PET-MRI', input: 'Amyloid PET + 같은 환자의 MRI', output: '영역별 Centiloid', foot: 'MRI 기준으로 정합' },
            ]}
          />
        </ProdSection>

        <IntegrationSection />

        <FaqSection
          alt
          items={[
            {
              q: '왜 Centiloid 단위를 사용하나요?',
              a: 'Centiloid(CL)는 ADNI·AIBL 등 국제 합의를 기반으로 한 아밀로이드 PET 정량 평가의 국제 표준 단위입니다. 장비·트레이서·기관이 달라도 단순 비교가 용이합니다.',
            },
            {
              q: '의료기관에 PET 장비가 없는데 사용 가능한가요?',
              a: '네. 환자가 외부 영상의학과에서 받은 PET 영상과 같은 검사의 CT 또는 MRI(CD 또는 PACS)를 함께 업로드하여 분석할 수 있습니다.',
            },
            {
              q: '설치형과 클라우드 중 선택할 수 있나요?',
              a: '네. 병원 내 서버에 설치하는 방식과 클라우드 방식을 같은 구조로 제공하며, 기존 PACS와 연동할 수 있습니다.',
            },
            {
              q: '분석 결과만으로 알츠하이머를 진단할 수 있나요?',
              a: 'Amylo는 PET 영상에서 정량화한 수치(Centiloid)를 제시하는 소프트웨어입니다. 양성/음성 등 임상 판정과 치료 방향 결정은 담당의 종합 판단에 따릅니다.',
            },
          ]}
        />
      </SolutionLayout>
    </PublicLayout>
  );
}

export const Route = createFileRoute('/solutions/amylo')({
  component: Page,
});
